/**
 * Share pages with organizations.
 *
 * Sharing is authoritative on the server (it does not travel through PowerSync upload):
 * the endpoints validate ownership and Postgres triggers set
 * page.is_shared, moving the page from the personal stream to the organization's
 * for all members.
 */
import { createSignal } from "solid-js";
import { type components, queryKeys } from "@apunte-digital/shared";
import { generateId } from "@fleet/utils";
import { api, createApiQuery, fetchApi } from "@/lib/api";

type OrganizationDTO = components["schemas"]["OrganizationDTO"];
type ErrorModel = components["schemas"]["ErrorModel"];

export function usePageSharing(pageId: () => string, options?: { enabled?: () => boolean }) {
  const [isToggling, setIsToggling] = createSignal<string | null>(null);

  const organizations = createApiQuery<OrganizationDTO[] | null, ErrorModel>(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
    () => ({ enabled: options?.enabled?.() ?? true }),
  );

  const sharedOrgIds = createApiQuery<string[] | null, ErrorModel>(
    () => queryKeys.pages.sharedOrgs(pageId()),
    async () =>
      api.GET("/api/sharing/getPageOrganizations", {
        params: { query: { pageId: pageId() } },
      }),
    () => ({ enabled: options?.enabled?.() ?? true }),
  );

  const isShared = (organizationId: string) => (sharedOrgIds.data ?? []).includes(organizationId);

  const toggleShare = async (organizationId: string) => {
    if (isToggling()) {
      return;
    }
    setIsToggling(organizationId);
    try {
      const request = isShared(organizationId)
        ? api.POST("/api/sharing/revokePageFromOrganization", {
            body: { pageId: pageId(), organizationId },
          })
        : api.POST("/api/sharing/sharePageWithOrganization", {
            body: { id: generateId(), pageId: pageId(), organizationId },
          });
      await fetchApi(request);
      await sharedOrgIds.refetch();
    } finally {
      setIsToggling(null);
    }
  };

  return { organizations, sharedOrgIds, isShared, toggleShare, isToggling };
}
